/**
 * Etapa 1 (bolsillo proforma): con un solo embarque, el adelanto va en una línea proforma+embarque
 * (no duplicar bolsillo solo-proforma + adelanto embarque). El excedente va a factura solo-embarque.
 */

function normalizeProformId(row, parentID) {
  const p = row && row.proform;
  if (p != null && p !== "") {
    if (typeof p === "object" && p._id != null) return String(p._id);
    return String(p);
  }
  return parentID != null && parentID !== "" ? String(parentID) : null;
}

function stripShippingFromRow(row) {
  const r = { ...row };
  delete r.shipping;
  return r;
}

function stripProformFromRow(row) {
  const r = { ...row };
  delete r.proform;
  return r;
}

/** Hueco de adelanto esperado en el embarque (etapa 2, zona adelanto). */
export function remainingAdvanceOnShip(ship) {
  const expected = Number(ship && ship.expectedAdvanceOnShipping);
  const assigned = Number(ship && ship.advanceAssignedAmount) || 0;
  if (!Number.isFinite(expected) || expected <= 0.01) return 0;
  return Math.max(0, parseFloat((expected - assigned).toFixed(2)));
}

/** Pendiente de cobro por factura de embarque (misma fila que «Paid & requested / total»). */
export function remainingInvoiceOnShip(ship) {
  const pending = Number(ship && ship.pendingToCollect);
  if (!Number.isFinite(pending) || pending <= 0.01) return 0;
  return parseFloat(pending.toFixed(2));
}

/**
 * Cupo restante en el embarque (total factura − adelanto ya asignado − cobro factura ya aplicado).
 * @returns {number|null} null si no hay totalProform en el contexto
 */
export function remainingOperRoomOnShip(ship) {
  const total = Number(ship && ship.totalProform);
  if (!Number.isFinite(total) || total <= 0.01) return null;
  const adv = Number(ship && ship.advanceAssignedAmount) || 0;
  const inv = Number(ship && ship.shippingOnlyCollectedAmount) || 0;
  return Math.max(0, parseFloat((total - adv - inv).toFixed(2)));
}

/**
 * Contexto para reparto etapa 1 → embarque en QuickAddCollectModal.
 * @param {{ extraParams?: object, parentModel?: string, collectionStagesVisual?: object, amountMax?: number }} opts
 */
export function buildStage1AutoShipSplitContext(opts = {}) {
  const type = opts.extraParams && opts.extraParams.type;
  if (type !== "advance") return null;
  const parent = String(opts.parentModel || "").toLowerCase();
  if (parent !== "proform") return null;
  const csv = opts.collectionStagesVisual;
  if (!csv || !csv.stage1) return null;
  const cap = Number(opts.amountMax);
  if (!Number.isFinite(cap) || cap <= 0.01) return null;
  const ships = (csv.stage2Shippings || []).map((s) => ({
    shippingId: s.shippingId,
    expectedAdvanceOnShipping: s.expectedAdvanceOnShipping,
    advanceAssignedAmount: s.advanceAssignedAmount,
    shippingOnlyCollectedAmount: s.shippingOnlyCollectedAmount,
    pendingToCollect: s.pendingToCollect,
    totalProform: s.totalProform,
    name: s.name || "",
  }));
  return { shippings: ships, stage1Cap: cap };
}

/**
 * Tope máximo aplicable en esta proforma con un solo embarque:
 * adelanto (proforma+embarque, un solo cupo) + factura pendiente del embarque.
 */
export function stage1SingleShipCollectLimits(stage1Cap, context) {
  const cap = parseFloat(Number(stage1Cap).toFixed(2));
  const ships = (context && context.shippings) || [];
  if (ships.length !== 1 || !Number.isFinite(cap) || cap < 0) {
    return { single: false };
  }
  const ship = ships[0];
  const advanceRoom = remainingAdvanceOnShip(ship);
  const invoiceRoom = remainingInvoiceOnShip(ship);
  const operRoom = remainingOperRoomOnShip(ship);
  const advanceSlot =
    cap > 0.005 && advanceRoom > 0.005
      ? parseFloat(Math.min(cap, advanceRoom).toFixed(2))
      : cap > 0.005
        ? cap
        : advanceRoom > 0.005
          ? advanceRoom
          : 0;
  const max =
    operRoom != null
      ? operRoom
      : parseFloat((advanceSlot + invoiceRoom).toFixed(2));
  return {
    single: true,
    max,
    stage1Cap: cap,
    advanceRoom,
    invoiceRoom,
    advanceSlot,
    operRoom,
    shipName: ship && ship.name ? String(ship.name) : "",
    shippingId: ship && ship.shippingId,
  };
}

function buildAppliedtoSplit(template, parentID, cap, advanceAmt, invoiceAmt, shippingId) {
  const proformId = normalizeProformId(template, parentID);
  const rows = [];

  const pocket = parseFloat(Number(cap).toFixed(2));
  if (pocket > 0.005) {
    const proformOnly = stripShippingFromRow({ ...template });
    if (proformId) proformOnly.proform = proformId;
    proformOnly.amount = pocket;
    rows.push(proformOnly);
  }

  const adv = parseFloat(Number(advanceAmt).toFixed(2));
  if (adv > 0.005 && shippingId != null && shippingId !== "") {
    const advLine = { ...template };
    if (proformId) advLine.proform = proformId;
    advLine.shipping = shippingId;
    advLine.amount = adv;
    rows.push(advLine);
  }

  const inv = parseFloat(Number(invoiceAmt).toFixed(2));
  if (inv > 0.005 && shippingId != null && shippingId !== "") {
    const invLine = stripProformFromRow({ ...template });
    invLine.shipping = shippingId;
    invLine.amount = inv;
    rows.push(invLine);
  }

  return rows;
}

/**
 * Reparte el excedente (monto − bolsillo etapa 1) en un solo embarque.
 * Prioriza una sola línea de embarque; solo usa adelanto + factura si el excedente no cabe entero en ninguno.
 */
function allocateExcessToSingleShip(excess, advanceRoom, invoiceRoom) {
  const ex = parseFloat(Number(excess).toFixed(2));
  const advCap = Math.max(0, parseFloat(Number(advanceRoom).toFixed(2)));
  const invCap = Math.max(0, parseFloat(Number(invoiceRoom).toFixed(2)));
  if (ex <= 0.005) {
    return { toAdvance: 0, toInvoice: 0 };
  }
  if (ex <= advCap + 0.02) {
    return { toAdvance: ex, toInvoice: 0 };
  }
  if (ex <= invCap + 0.02) {
    return { toAdvance: 0, toInvoice: ex };
  }
  const toAdvance = advCap;
  const toInvoice = parseFloat(Math.min(ex - advCap, invCap).toFixed(2));
  return { toAdvance, toInvoice };
}

/**
 * Reparte automáticamente appliedto al cobrar desde proforma (anticipo):
 * - El monto del cobro puede ser mayor (saldo del cliente / varias operaciones).
 * - Un solo embarque: adelanto en línea proforma+embarque; resto en factura solo-embarque.
 * - Sin embarque o varios: solo bolsillo proforma (pendiente etapa 1).
 *
 * @param {number} amount
 * @param {number} stage1Cap - pendiente adelanto etapa 1 (amountMax del modal)
 * @param {Array} appliedto
 * @param {{ shippings?: Array }} context
 * @param {string} parentID
 */
export function allocateProformCollectAppliedto(amount, stage1Cap, appliedto, context, parentID) {
  const amt = parseFloat(Number(amount).toFixed(2));
  if (!Number.isFinite(amt) || amt < 0.01) {
    return { ok: false, reason: "invalid_amount" };
  }

  const template =
    Array.isArray(appliedto) && appliedto.length > 0 && typeof appliedto[0] === "object"
      ? appliedto[0]
      : {};

  const stage1Room =
    Number.isFinite(Number(stage1Cap)) && Number(stage1Cap) > 0.01
      ? parseFloat(Number(stage1Cap).toFixed(2))
      : 0;

  let remaining = amt;
  let pocket = 0;
  let toAdvance = 0;
  let toInvoice = 0;
  let shippingId = null;

  const ships = (context && context.shippings) || [];

  if (ships.length === 1) {
    const ship = ships[0];
    shippingId = ship.shippingId;
    const advanceRoom = remainingAdvanceOnShip(ship);
    const invoiceRoom = remainingInvoiceOnShip(ship);
    let operRoomLeft = remainingOperRoomOnShip(ship);

    let advanceCap = 0;
    if (stage1Room > 0.005 && advanceRoom > 0.005) {
      advanceCap = parseFloat(Math.min(stage1Room, advanceRoom).toFixed(2));
    } else if (advanceRoom > 0.005) {
      advanceCap = parseFloat(Number(advanceRoom).toFixed(2));
    } else if (stage1Room > 0.005) {
      advanceCap = stage1Room;
    }

    if (advanceCap > 0.005) {
      const advLimit =
        operRoomLeft != null ? Math.min(advanceCap, operRoomLeft) : advanceCap;
      toAdvance = Math.min(remaining, advLimit);
      remaining = parseFloat((remaining - toAdvance).toFixed(2));
      if (operRoomLeft != null) {
        operRoomLeft = parseFloat((operRoomLeft - toAdvance).toFixed(2));
      }
    }

    if (remaining > 0.005 && invoiceRoom > 0.005) {
      let invoiceCap = invoiceRoom;
      if (operRoomLeft != null) {
        invoiceCap = Math.min(invoiceRoom, operRoomLeft);
      }
      if (invoiceCap > 0.005) {
        toInvoice = Math.min(remaining, invoiceCap);
        remaining = parseFloat((remaining - toInvoice).toFixed(2));
      }
    }
  } else {
    pocket = Math.min(remaining, stage1Room);
    remaining = parseFloat((remaining - pocket).toFixed(2));
  }

  const appliedtoRows = buildAppliedtoSplit(
    template,
    parentID,
    pocket,
    toAdvance,
    toInvoice,
    shippingId
  );

  const allocatedTotal = parseFloat((pocket + toAdvance + toInvoice).toFixed(2));
  const unallocated = parseFloat((amt - allocatedTotal).toFixed(2));

  return {
    ok: true,
    amount: amt,
    appliedto: appliedtoRows,
    allocatedTotal,
    unallocated: unallocated > 0.005 ? unallocated : 0,
  };
}

/** @deprecated Usar allocateProformCollectAppliedto */
export function trySplitStage1CollectOverCap(amount, stage1Cap, appliedto, context, parentID) {
  const result = allocateProformCollectAppliedto(
    amount,
    stage1Cap,
    appliedto,
    context,
    parentID
  );
  if (!result.ok) return result;
  return {
    ...result,
    split: result.appliedto.length > 1,
    noSplit: result.appliedto.length <= 1,
  };
}
